import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SectionTitle from "@/components/SectionTitle";
import { Newspaper, Copy, RefreshCw, Target, ShieldAlert, TrendingUp, Zap, LineChart, Star, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import Sparkline from "@/components/Sparkline";
import LivePrice from "@/components/LivePrice";
import StockChartModal from "@/components/StockChartModal";
import AnalysisModal from "@/components/AnalysisModal";

const sentimentColor = (s) => {
  const v = (s || "").toLowerCase();
  if (v.includes("bull")) return "text-bull";
  if (v.includes("bear")) return "text-bear";
  return "text-amber";
};

const cueDot = (s) => {
  const v = (s || "").toLowerCase();
  if (v.includes("posit")) return "bg-[var(--bull)]";
  if (v.includes("negat")) return "bg-[var(--bear)]";
  return "bg-[var(--amber)]";
};

const RecoCard = ({ r, idx, onChart, onAnalyze }) => {
  const copy = () => {
    const txt = `${r.ticker} - ${r.action} (${r.timeframe})\nEntry: ${r.entry}\nTP1: ${r.tp1} | TP2: ${r.tp2}\nStop Loss: ${r.stop_loss}\nRRR: ${r.risk_reward}\nKatalis: ${r.catalyst}\n\n- FAS Trading`;
    navigator.clipboard.writeText(txt)
      .then(() => toast.success("Rekomendasi disalin ke clipboard!"))
      .catch(() => toast.error("Gagal menyalin ke clipboard"));
  };
  return (
    <motion.div
      whileHover={{ y: -3 }}
      transition={{ duration: 0.15 }}
      className="card p-5 relative overflow-hidden"
      data-testid={`reco-card-${idx}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-heading text-2xl font-extrabold tracking-tight">{r.ticker}</span>
            <span className="px-2 py-0.5 rounded-md bg-[var(--bull)]/15 text-bull text-xs font-bold border border-[var(--bull)]/30">{r.action}</span>
          </div>
          <p className="text-xs text-[var(--text-secondary)] mt-0.5">{r.name} · {r.timeframe}</p>
        </div>
        <button onClick={copy} data-testid={`copy-reco-${idx}`} className="p-2 rounded-md bg-[var(--surface-hover)] hover:bg-[var(--accent)]/20 text-[var(--text-secondary)] hover:text-accent transition-colors">
          <Copy size={15} />
        </button>
      </div>
      <button onClick={() => onChart(r.ticker, r.name)} data-testid={`chart-reco-${idx}`} className="w-full mb-2 h-9 rounded-lg bg-[var(--surface-hover)] border border-[var(--border)] text-sm font-semibold flex items-center justify-center gap-2 hover:border-[var(--accent)]/40 hover:text-accent transition-colors">
        <LineChart size={15} /> Buka Grafik Detail (Candle + RSI)
      </button>
      <button onClick={() => onAnalyze(r.ticker)} data-testid={`analyze-reco-${idx}`} className="w-full mb-3 h-9 rounded-lg bg-[var(--accent)]/12 border border-[var(--accent)]/30 text-accent text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[var(--accent)]/20 transition-colors">
        <Sparkles size={15} /> Analisis AI (Skor 4 Pilar)
      </button>
      <div className="mb-4 bg-[var(--surface-base)] rounded-lg p-2.5 border border-[var(--border-subtle)]">
        <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Pergerakan Harga (live, ~5 hari)</p>
        <Sparkline ticker={r.ticker} />
      </div>
      <LivePrice ticker={r.ticker} className="mb-3" />
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="bg-[var(--surface-base)] rounded-lg p-3 border border-[var(--border-subtle)]">
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Entry Area</p>
          <p className="font-mono-data font-bold">{r.entry}</p>
        </div>
        <div className="bg-[var(--surface-base)] rounded-lg p-3 border border-[var(--border-subtle)]">
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1 flex items-center gap-1"><ShieldAlert size={10} /> Stop Loss</p>
          <p className="font-mono-data font-bold text-bear">{r.stop_loss}</p>
        </div>
        <div className="bg-[var(--surface-base)] rounded-lg p-3 border border-[var(--border-subtle)]">
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1 flex items-center gap-1"><Target size={10} /> TP1</p>
          <p className="font-mono-data font-bold text-bull">{r.tp1}</p>
        </div>
        <div className="bg-[var(--surface-base)] rounded-lg p-3 border border-[var(--border-subtle)]">
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1 flex items-center gap-1"><Target size={10} /> TP2</p>
          <p className="font-mono-data font-bold text-bull">{r.tp2}</p>
        </div>
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-[var(--border-subtle)]">
        <span className="text-xs text-[var(--text-secondary)]">Risk/Reward</span>
        <span className="font-mono-data font-bold text-accent">{r.risk_reward}</span>
      </div>
      <p className="text-xs text-[var(--text-secondary)] mt-3 leading-relaxed"><span className="text-accent font-semibold">Katalis:</span> {r.catalyst}</p>
    </motion.div>
  );
};

export default function BriefingSection() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [chart, setChart] = useState(null);
  const [analyze, setAnalyze] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const syncWatchlist = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/watchlist/sync-recommendations");
      toast.success(res.data.added > 0 ? `${res.data.added} saham ditambahkan ke watchlist` : "Semua rekomendasi sudah ada di watchlist");
      window.dispatchEvent(new Event("watchlist-updated"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyinkronkan");
    } finally {
      setSyncing(false);
    }
  };
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [nowTick, setNowTick] = useState(Date.now());

  useEffect(() => {
    if (cooldownUntil <= Date.now()) return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [cooldownUntil]);

  const cooldownLeft = Math.max(0, Math.ceil((cooldownUntil - nowTick) / 1000));

  const load = async () => {
    try {
      const res = await api.get("/briefing");
      setData(res.data);
    } catch (e) {
      toast.error("Gagal memuat briefing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const refresh = async () => {
    if (cooldownLeft > 0) {
      toast.info(`Tunggu ${cooldownLeft} detik lagi sebelum memperbarui.`);
      return;
    }
    setRefreshing(true);
    try {
      const res = await api.post("/briefing/refresh");
      setData((d) => ({ ...res.data, next_update_wib: d?.next_update_wib }));
      setCooldownUntil(Date.now() + (res.data.refresh_cooldown || 300) * 1000);
      toast.success("Briefing diperbarui");
    } catch (e) {
      if (e?.response?.status === 429) {
        const detail = e.response.data?.detail || "Terlalu sering. Coba lagi nanti.";
        const m = /(\d+)/.exec(detail);
        if (m) setCooldownUntil(Date.now() + parseInt(m[1], 10) * 1000);
        toast.error(detail);
      } else {
        toast.error("Gagal memperbarui briefing");
      }
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <section id="briefing" className="scroll-mt-20">
      <SectionTitle
        eyebrow="Update Otomatis 08:30 WIB"
        title="Briefing FAS Trading Hari Ini"
        desc="Ringkasan pra-pembukaan pasar, sentimen global, dan 2 rekomendasi saham inti yang diperbarui otomatis setiap pagi."
        icon={Newspaper}
      />

      {loading ? (
        <div className="card p-10 text-center text-[var(--text-secondary)]">
          <div className="w-8 h-8 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          Menyusun briefing AI...
        </div>
      ) : data ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 card p-5">
            <div className="flex items-center justify-between mb-4">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--bull)]/12 text-bull text-[11px] font-bold border border-[var(--bull)]/25">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--bull)] animate-pulse" /> BRIEFING SIAP
              </span>
              <button onClick={refresh} disabled={refreshing || cooldownLeft > 0} title={cooldownLeft > 0 ? `Tunggu ${cooldownLeft} detik` : "Perbarui briefing"} data-testid="refresh-briefing-btn" className={`inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md hover:bg-[var(--surface-hover)] text-[var(--text-secondary)] hover:text-accent transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${user ? "" : "hidden"}`}>
                <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
                {cooldownLeft > 0 && <span className="font-mono-data text-[11px]" data-testid="refresh-cooldown">{Math.floor(cooldownLeft / 60)}:{String(cooldownLeft % 60).padStart(2, "0")}</span>}
              </button>
            </div>
            <p className="text-xs uppercase tracking-wider text-[var(--text-muted)] mb-1">Sentimen Pasar</p>
            <p className={`font-heading text-3xl font-extrabold ${sentimentColor(data.market_sentiment)}`} data-testid="market-sentiment">{data.market_sentiment}</p>
            <div className="mt-4">
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-[var(--text-muted)]">Skor Sentimen</span>
                <span className="font-mono-data font-bold text-accent">{data.sentiment_score}/100</span>
              </div>
              <div className="h-2 rounded-full bg-[var(--surface-base)] overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[var(--amber)] to-[var(--accent)]" style={{ width: `${data.sentiment_score}%` }} />
              </div>
            </div>
            <p className="text-sm text-[var(--text-secondary)] mt-4 leading-relaxed">{data.ihsg_view}</p>
            <div className="mt-4 space-y-2">
              {(data.global_cues || []).map((c, i) => (
                <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-[var(--border-subtle)] last:border-0">
                  <span className="flex items-center gap-2 text-[var(--text-secondary)]">
                    <span className={`w-1.5 h-1.5 rounded-full ${cueDot(c.status)}`} />
                    {c.name}
                  </span>
                  <span className="font-mono-data font-semibold">{c.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2 space-y-4">
            <div className="card p-5">
              <div className="flex items-center gap-2 mb-2">
                <Zap size={15} className="text-accent" />
                <h3 className="font-heading font-semibold">Narasi Pasar</h3>
              </div>
              <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{data.narrative}</p>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <TrendingUp size={16} className="text-accent" />
              <h3 className="font-heading font-semibold">2 Rekomendasi Saham Inti</h3>
              {user && (
                <button onClick={syncWatchlist} disabled={syncing} data-testid="sync-watchlist-btn" className="ml-auto text-xs font-semibold px-3 py-1.5 rounded-lg bg-[var(--accent)]/12 border border-[var(--accent)]/30 text-accent hover:bg-[var(--accent)]/20 disabled:opacity-60 flex items-center gap-1.5">
                  <Star size={13} /> {syncing ? "Menambahkan..." : "+ Watchlist"}
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(data.recommendations || []).map((r, i) => (
                <RecoCard key={i} r={r} idx={i} onChart={(t, n) => setChart({ ticker: t, name: n })} onAnalyze={(t) => setAnalyze(t)} />
              ))}
            </div>
          </div>
        </div>
      ) : null}
      {chart && <StockChartModal ticker={chart.ticker} name={chart.name} onClose={() => setChart(null)} />}
      {analyze && <AnalysisModal ticker={analyze} onClose={() => setAnalyze(null)} />}
    </section>
  );
}
